import type { PnlSwap } from './api-types'
import { SOL_MINT, USDC_MINT, USDT_MINT, tokenLogo } from './default-tokens'
import { formatSol, formatUsd } from './format'

/*
 * P&L (John, Nov 2 2026): what the account's trades of a token brought in,
 * less what its buys cost, in dollars and in SOL, over his accounting
 * periods. Days are Central calendar days; weeks turn over Friday 8:30 AM
 * Central, the weeks his HELL and Golden Kitty tallies use. Only finished
 * trades of a token against SOL or a stablecoin count: moving between SOL
 * and a stablecoin, deposits and withdrawals are neither profit nor loss.
 * The trades are the same records the Transactions table and the reports
 * show; a sell is worth what came back, a buy what went in, each priced
 * when the trade ran.
 */

export type PnlRange = 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'all'

export const PNL_RANGES: { value: PnlRange; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'thisWeek', label: 'This week' },
  { value: 'lastWeek', label: 'Last week' },
  { value: 'all', label: 'All time' },
]

/** the strategies' orders, hand swaps, or both */
export type PnlSource = 'both' | 'strategies' | 'swaps'

export const PNL_SOURCES: { value: PnlSource; label: string }[] = [
  { value: 'both', label: 'Both' },
  { value: 'strategies', label: 'Strategies' },
  { value: 'swaps', label: 'Swaps' },
]

export const PNL_ZONE = 'America/Chicago'
export const PNL_ZONE_LABEL = 'Central Time'
/** weeks turn over at this wall-clock moment in Central: Friday 8:30 AM */
export const WEEK_TURNOVER = { weekday: 5, hour: 8, minute: 30 }

type Wall = { year: number; month: number; day: number; hour: number; minute: number; weekday: number }

const WEEKDAYS: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }
const wallFormat = new Intl.DateTimeFormat('en-US', {
  timeZone: PNL_ZONE,
  hourCycle: 'h23',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  weekday: 'short',
})

/** what a clock in Central reads at the instant */
export function wallClock(at: Date): Wall {
  const parts: Record<string, string> = {}
  for (const part of wallFormat.formatToParts(at)) parts[part.type] = part.value
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    // some engines write midnight as 24 under h23
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    weekday: WEEKDAYS[parts.weekday] ?? 0,
  }
}

/** the instant at which a Central clock reads the given wall time; the day may run past the month's end or before its start */
export function zonedInstant(year: number, month: number, day: number, hour: number, minute: number): Date {
  const wanted = Date.UTC(year, month - 1, day, hour, minute)
  let guess = wanted
  // the zone's offset at the guess corrects it; a second pass settles a guess that fell across a clock change
  for (let i = 0; i < 2; i++) {
    const w = wallClock(new Date(guess))
    guess += wanted - Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute)
  }
  return new Date(guess)
}

/** the most recent Friday 8:30 AM Central at or before the instant */
export function weekStart(now: Date): Date {
  const w = wallClock(now)
  const back = (w.weekday - WEEK_TURNOVER.weekday + 7) % 7
  const candidate = zonedInstant(w.year, w.month, w.day - back, WEEK_TURNOVER.hour, WEEK_TURNOVER.minute)
  // a Friday morning before the turnover still belongs to the week before
  return candidate.getTime() > now.getTime() ? zonedInstant(w.year, w.month, w.day - back - 7, WEEK_TURNOVER.hour, WEEK_TURNOVER.minute) : candidate
}

export type PnlBounds = { from: Date | null; to: Date | null }

/** the period as instants, `to` exclusive: days are Central calendar days, weeks Friday 8:30 AM to Friday 8:30 AM */
export function pnlRangeBounds(range: PnlRange, now = new Date()): PnlBounds {
  const w = wallClock(now)
  const midnight = (daysAgo: number) => zonedInstant(w.year, w.month, w.day - daysAgo, 0, 0)
  switch (range) {
    case 'today':
      return { from: midnight(0), to: null }
    case 'yesterday':
      return { from: midnight(1), to: midnight(0) }
    case 'thisWeek':
      return { from: weekStart(now), to: null }
    case 'lastWeek': {
      const start = weekStart(now)
      return { from: weekStart(new Date(start.getTime() - 1)), to: start }
    }
    default:
      return { from: null, to: null }
  }
}

const edgeFormat = new Intl.DateTimeFormat('en-US', { timeZone: PNL_ZONE, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })

/** "Fri Oct 30, 8:30 AM – now", "Sun Nov 1, 12:00 AM – Mon Nov 2, 12:00 AM"; nothing for all time */
export function describeBounds(bounds: PnlBounds): string | null {
  if (!bounds.from) return null
  const text = (at: Date) => edgeFormat.format(at).replace(/[  ]/g, ' ')
  return `${text(bounds.from)} – ${bounds.to ? text(bounds.to) : 'now'}`
}

const SETTLEMENT = new Set([SOL_MINT, USDC_MINT, USDT_MINT])

export type PnlTrade = {
  id: string
  at: Date
  token: string
  symbol: string
  imageUrl?: string
  side: 'buy' | 'sell'
  /** an order of an automated strategy, else a hand swap */
  strategy: boolean
  /** a sell: the dollars it brought in; a buy: the dollars it cost */
  usd: number | null
  /** the same in SOL; null when the trade settled in a stablecoin and no SOL price was on record */
  sol: number | null
}

/** the finished trades of a token against SOL or a stablecoin, as the P&L counts them (swap.pnlTrades: confirmed Solana swaps only) */
export function pnlTrades(swaps: PnlSwap[]): PnlTrade[] {
  const out: PnlTrade[] = []
  for (const h of swaps) {
    if (!h.createdAt) continue
    // SOL to USDC and back is a conversion, not a trade
    if (SETTLEMENT.has(h.tokenAddress)) continue
    const sell = h.side === 'sell'
    const usd = sell ? (h.outputAmountUsd ?? h.inputAmountUsd ?? null) : (h.inputAmountUsd ?? h.outputAmountUsd ?? null)
    const settlementMint = sell ? h.outputTokenAddress : h.inputTokenAddress
    const settlementUi = sell ? h.outputAmountUi : h.inputAmountUi
    const sol = settlementMint === SOL_MINT && settlementUi !== undefined ? settlementUi : usd !== null && h.nativePriceUsd ? usd / h.nativePriceUsd : null
    out.push({
      id: h.swapId,
      at: new Date(h.createdAt),
      token: h.tokenAddress,
      symbol: (h.tokenSymbol ?? '').toUpperCase() || 'TOKEN',
      imageUrl: h.imageUrl ?? tokenLogo(h.tokenAddress),
      side: sell ? 'sell' : 'buy',
      strategy: h.strategyId !== null && h.strategyId !== undefined,
      usd,
      sol,
    })
  }
  return out
}

export type PnlToken = { key: string; symbol: string; imageUrl?: string; trades: number }

/** every token traded, busiest first */
export function pnlTokens(trades: PnlTrade[]): PnlToken[] {
  const map = new Map<string, PnlToken>()
  for (const t of trades) {
    const token = map.get(t.token) ?? { key: t.token, symbol: t.symbol, imageUrl: t.imageUrl, trades: 0 }
    token.trades += 1
    if (!token.imageUrl && t.imageUrl) token.imageUrl = t.imageUrl
    map.set(t.token, token)
  }
  return [...map.values()].sort((a, b) => b.trades - a.trades || a.symbol.localeCompare(b.symbol))
}

export type PnlFilter = PnlBounds & { token: string | null; source: PnlSource }

export type PnlTotals = {
  netUsd: number
  netSol: number
  soldUsd: number
  soldSol: number
  sells: number
  boughtUsd: number
  boughtSol: number
  buys: number
  /** trades that carry no dollar value, left out of the dollar figures */
  unpricedUsd: number
  /** trades whose SOL side could not be worked out, left out of the SOL figures */
  unpricedSol: number
}

/** sold less bought over the trades the filter keeps */
export function pnlTotals(trades: PnlTrade[], f: PnlFilter): PnlTotals {
  const t: PnlTotals = { netUsd: 0, netSol: 0, soldUsd: 0, soldSol: 0, sells: 0, boughtUsd: 0, boughtSol: 0, buys: 0, unpricedUsd: 0, unpricedSol: 0 }
  for (const trade of trades) {
    if (f.token !== null && trade.token !== f.token) continue
    if (f.source === 'strategies' ? !trade.strategy : f.source === 'swaps' ? trade.strategy : false) continue
    if (f.from && trade.at < f.from) continue
    if (f.to && trade.at >= f.to) continue
    const sell = trade.side === 'sell'
    if (sell) t.sells += 1
    else t.buys += 1
    if (trade.usd === null) t.unpricedUsd += 1
    else {
      if (sell) t.soldUsd += trade.usd
      else t.boughtUsd += trade.usd
      t.netUsd += sell ? trade.usd : -trade.usd
    }
    if (trade.sol === null) t.unpricedSol += 1
    else {
      if (sell) t.soldSol += trade.sol
      else t.boughtSol += trade.sol
      t.netSol += sell ? trade.sol : -trade.sol
    }
  }
  return t
}

/** "+$1,234.56", "-$12.00", "$0.00": the sign from the rounded cents, so a fraction of a cent reads as zero */
export function signedUsd(value: number): string {
  const cents = Math.round(value * 100)
  const sign = cents > 0 ? '+' : cents < 0 ? '-' : ''
  return `${sign}${formatUsd(Math.abs(cents) / 100)}`
}

/** "+12.34567 SOL", "-0.5 SOL", "0 SOL" */
export function signedSol(value: number): string {
  const rounded = Math.round(value * 1e5) / 1e5
  const sign = rounded > 0 ? '+' : rounded < 0 ? '-' : ''
  return `${sign}${formatSol(Math.abs(rounded))} SOL`
}

export type PnlChoices = { token: string | null; range: PnlRange; source: PnlSource }

// Remembered per browser; a convenience, so storage failures just mean the defaults.
const CHOICES_KEY = 'dispence.pnl'

export function readChoices(): PnlChoices | null {
  try {
    const raw = window.localStorage.getItem(CHOICES_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<PnlChoices>
    const range = PNL_RANGES.some((r) => r.value === parsed.range) ? (parsed.range as PnlRange) : 'today'
    const source = PNL_SOURCES.some((s) => s.value === parsed.source) ? (parsed.source as PnlSource) : 'both'
    return { token: typeof parsed.token === 'string' ? parsed.token : null, range, source }
  } catch {
    return null
  }
}

export function writeChoices(choices: PnlChoices) {
  try {
    window.localStorage.setItem(CHOICES_KEY, JSON.stringify(choices))
  } catch {
    // private mode or blocked storage: the choice lasts for this tab
  }
}
